export interface TrendLine {
  slope: number;
  intercept: number;
  points: Array<{ x: number; y: number }>;
}

export interface Insight {
  type: 'positive' | 'negative' | 'neutral' | 'alert' | 'success' | 'info' | 'warning';
  title: string;
  description: string;
  metric?: string | number;
  value?: number;
}

export interface RankedData {
  best: Array<{ id: string; name: string; value: number }>;
  worst: Array<{ id: string; name: string; value: number }>;
}

/**
 * Calcular regressão linear simples
 */
export function calculateTrend(data: Array<{ x: number; y: number }>): TrendLine {
  const n = data.length;
  if (n === 0) return { slope: 0, intercept: 0, points: [] };

  const sumX = data.reduce((sum, point) => sum + point.x, 0);
  const sumY = data.reduce((sum, point) => sum + point.y, 0);
  const sumXY = data.reduce((sum, point) => sum + point.x * point.y, 0);
  const sumXX = data.reduce((sum, point) => sum + point.x * point.x, 0);

  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  const points = data.map(point => ({
    x: point.x,
    y: slope * point.x + intercept
  }));

  return { slope, intercept, points };
}

/**
 * Detectar anomalias usando método Z-score
 */
export function detectAnomalies(data: number[], threshold: number = 2): boolean[] {
  if (data.length === 0) return [];

  const mean = data.reduce((sum, val) => sum + val, 0) / data.length;
  const stdDev = Math.sqrt(
    data.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / data.length
  );

  if (stdDev === 0) return data.map(() => false);

  return data.map(value => {
    const zScore = Math.abs((value - mean) / stdDev);
    return zScore > threshold;
  });
}

/**
 * Calcular variação percentual
 */
export function calculateVariation(current: number, previous: number): number {
  if (previous === 0) return current > 0 ? 100 : 0;
  return ((current - previous) / previous) * 100;
}

/**
 * Identificar top/bottom performers
 */
export function rankPerformance(
  data: Array<{ id: string; name: string; value: number }>,
  topN: number = 3
): RankedData {
  const sorted = [...data].sort((a, b) => b.value - a.value);
  
  return {
    best: sorted.slice(0, topN),
    worst: sorted.slice(-topN).reverse()
  };
}

/**
 * Gerar insights automáticos baseados em dados comparativos
 */
export function generateInsights(
  elements: Array<{
    name: string;
    metrics: {
      total_cost?: number;
      avg_efficiency?: number;
      alerts_count?: number;
      total_distance?: number;
    };
    variation?: {
      cost?: number;
      efficiency?: number;
    };
  }>
): Insight[] {
  const insights: Insight[] = [];

  if (elements.length < 2) return insights;

  // Encontrar maior custo
  const maxCost = elements.reduce((max, el) => 
    (el.metrics.total_cost || 0) > max.value 
      ? { name: el.name, value: el.metrics.total_cost || 0 }
      : max
  , { name: '', value: 0 });

  if (maxCost.value > 0) {
    insights.push({
      type: 'negative',
      title: 'Maior Custo Total',
      description: `${maxCost.name} apresenta o maior custo total`,
      metric: 'cost',
      value: maxCost.value
    });
  }

  // Encontrar melhor eficiência
  const maxEfficiency = elements.reduce((max, el) => 
    (el.metrics.avg_efficiency || 0) > max.value 
      ? { name: el.name, value: el.metrics.avg_efficiency || 0 }
      : max
  , { name: '', value: 0 });

  if (maxEfficiency.value > 0) {
    insights.push({
      type: 'positive',
      title: 'Melhor Eficiência',
      description: `${maxEfficiency.name} tem a melhor eficiência de combustível`,
      metric: 'efficiency',
      value: maxEfficiency.value
    });
  }

  // Encontrar menor número de alertas
  const minAlerts = elements.reduce((min, el) => 
    (el.metrics.alerts_count || 0) < min.value || min.value === -1
      ? { name: el.name, value: el.metrics.alerts_count || 0 }
      : min
  , { name: '', value: -1 });

  if (minAlerts.value >= 0) {
    insights.push({
      type: 'positive',
      title: 'Menos Alertas',
      description: `${minAlerts.name} apresenta o menor número de alertas`,
      metric: 'alerts',
      value: minAlerts.value
    });
  }

  // Analisar variações
  elements.forEach(el => {
    if (el.variation?.cost && el.variation.cost < -10) {
      insights.push({
        type: 'positive',
        title: 'Redução de Custos',
        description: `${el.name} reduziu custos em ${Math.abs(el.variation.cost).toFixed(1)}%`,
        metric: 'cost',
        value: el.variation.cost
      });
    }
    
    if (el.variation?.cost && el.variation.cost > 15) {
      insights.push({
        type: 'negative',
        title: 'Aumento de Custos',
        description: `${el.name} teve aumento de custos de ${el.variation.cost.toFixed(1)}%`,
        metric: 'cost',
        value: el.variation.cost
      });
    }

    if (el.variation?.efficiency && el.variation.efficiency > 10) {
      insights.push({
        type: 'positive',
        title: 'Melhoria de Eficiência',
        description: `${el.name} melhorou eficiência em ${el.variation.efficiency.toFixed(1)}%`,
        metric: 'efficiency',
        value: el.variation.efficiency
      });
    }
  });

  return insights.slice(0, 5); // Retornar até 5 insights
}

/**
 * Calcular média móvel
 */
export function calculateMovingAverage(data: number[], window: number = 3): number[] {
  const result: number[] = [];
  
  for (let i = 0; i < data.length; i++) {
    const start = Math.max(0, i - window + 1);
    const subset = data.slice(start, i + 1);
    const avg = subset.reduce((sum, val) => sum + val, 0) / subset.length;
    result.push(avg);
  }
  
  return result;
}

/**
 * Projetar valores futuros baseado em tendência
 */
export function projectFuture(trend: TrendLine, periods: number, lastX: number): number[] {
  const projections: number[] = [];
  
  for (let i = 1; i <= periods; i++) {
    const x = lastX + i;
    const y = trend.slope * x + trend.intercept;
    projections.push(Math.max(0, y)); // Evitar valores negativos
  }
  
  return projections;
}

/**
 * Calculate correlation coefficient between two datasets
 * @param dataX First dataset
 * @param dataY Second dataset
 * @returns Pearson correlation coefficient (-1 to 1)
 */
export function calculateCorrelation(dataX: number[], dataY: number[]): number {
  if (dataX.length !== dataY.length || dataX.length === 0) return 0;
  
  const n = dataX.length;
  const meanX = dataX.reduce((a, b) => a + b, 0) / n;
  const meanY = dataY.reduce((a, b) => a + b, 0) / n;
  
  let numerator = 0;
  let sumSqX = 0;
  let sumSqY = 0;
  
  for (let i = 0; i < n; i++) {
    const deltaX = dataX[i] - meanX;
    const deltaY = dataY[i] - meanY;
    numerator += deltaX * deltaY;
    sumSqX += deltaX * deltaX;
    sumSqY += deltaY * deltaY;
  }
  
  const denominator = Math.sqrt(sumSqX * sumSqY);
  return denominator === 0 ? 0 : numerator / denominator;
}

/**
 * Detect seasonal patterns in time series data
 * @param data Time series data
 * @returns Object with seasonality indicators
 */
export function detectSeasonality(data: number[]): {
  hasSeasonality: boolean;
  pattern: 'weekly' | 'monthly' | 'none';
  confidence: number;
} {
  if (data.length < 12) {
    return { hasSeasonality: false, pattern: 'none', confidence: 0 };
  }
  
  // Simple autocorrelation check
  const weeklyLag = Math.min(7, Math.floor(data.length / 3));
  const monthlyLag = Math.min(30, Math.floor(data.length / 2));
  
  const weeklyCorr = Math.abs(calculateCorrelation(
    data.slice(0, -weeklyLag),
    data.slice(weeklyLag)
  ));
  
  const monthlyCorr = data.length >= monthlyLag * 2 ? Math.abs(calculateCorrelation(
    data.slice(0, -monthlyLag),
    data.slice(monthlyLag)
  )) : 0;
  
  if (monthlyCorr > 0.6) {
    return { hasSeasonality: true, pattern: 'monthly', confidence: monthlyCorr };
  }
  if (weeklyCorr > 0.6) {
    return { hasSeasonality: true, pattern: 'weekly', confidence: weeklyCorr };
  }
  
  return { hasSeasonality: false, pattern: 'none', confidence: 0 };
}

/**
 * Calculate performance index (0-100) based on multiple metrics
 * @param metrics Object with metric values
 * @param weights Object with metric weights
 * @returns Performance index
 */
export function calculatePerformanceIndex(
  metrics: Record<string, number>,
  weights: Record<string, number> = {}
): number {
  const keys = Object.keys(metrics);
  if (keys.length === 0) return 0;
  
  const defaultWeight = 1 / keys.length;
  let totalScore = 0;
  let totalWeight = 0;
  
  keys.forEach(key => {
    const weight = weights[key] ?? defaultWeight;
    const normalizedValue = Math.max(0, Math.min(100, metrics[key]));
    totalScore += normalizedValue * weight;
    totalWeight += weight;
  });
  
  return totalWeight > 0 ? totalScore / totalWeight : 0;
}

/**
 * Generate actionable recommendations based on insights
 * @param insights Array of insights
 * @returns Prioritized recommendations
 */
export function generateActionableRecommendations(
  insights: Insight[]
): Array<{
  priority: 'critical' | 'high' | 'medium' | 'low';
  title: string;
  action: string;
  impact: string;
  link?: string;
}> {
  const recommendations: Array<{
    priority: 'critical' | 'high' | 'medium' | 'low';
    title: string;
    action: string;
    impact: string;
    link?: string;
  }> = [];
  
  insights.forEach(insight => {
    if ((insight.type === 'alert' || insight.type === 'negative') && insight.title.includes('custo')) {
      recommendations.push({
        priority: 'critical',
        title: 'Reduzir Custos Operacionais',
        action: 'Investigar e otimizar os itens com maior custo identificados',
        impact: 'Redução estimada de 15-20% nos custos operacionais',
        link: '/fleet/costs'
      });
    }
    
    if ((insight.type === 'success' || insight.type === 'positive') && insight.title.includes('eficiência')) {
      recommendations.push({
        priority: 'medium',
        title: 'Replicar Boas Práticas',
        action: 'Documentar e compartilhar as práticas dos itens mais eficientes',
        impact: 'Melhoria de 10-15% na eficiência geral',
        link: '/fleet/reports'
      });
    }
    
    if (insight.value && Math.abs(insight.value) > 50) {
      recommendations.push({
        priority: 'high',
        title: 'Atenção a Variações Significativas',
        action: 'Analisar as causas das grandes variações identificadas',
        impact: 'Prevenção de problemas futuros e estabilização de métricas'
      });
    }
  });
  
  return recommendations.sort((a, b) => {
    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });
}
